/**
 * The Cockpit at `/` — the designer's synthesis page (brief part 1 §6, DEC-87, package C7).
 *
 * ── THE BANDS, IN ORDER ──────────────────────────────────────────────────────
 *
 *   1  Hero (`CockpitHero`), 150px: a black-and-white photograph, four rules, no caption; a kicker
 *      with the greeting, the market and the week; the synthesis sentence as the title.
 *   —  The page's ONE status strip (`PageStatusRegion`, brief §4), under the hero: every read's
 *      failure, refusal or absence registers there (`CockpitStatus`) — about twenty notices
 *      became one.
 *   2  Four figures (`CockpitKpis`), a 2px ink rule under them.
 *   3  Two columns: «Da decidere» (`NeedsDecision`) and «Copertura dati» (`DataCoverage`).
 *   4  One tile per app (`AppTiles`), the app's state on each.
 *
 * The run is NOT on the page: it is in the top bar (`RunScopeInTopBar`, designer item 2.7), with
 * the run picker in the run chip's popover.
 *
 * ── SYNTHESIS ONLY (DEC-87, package A 2026-09-30) ─────────────────────────────
 *
 * The client's third rule is "one page, one function". Everything operational the Cockpit carried
 * moved to the page of its app (ADR 9017, amendment 2026-09-30): the price queue, the execution
 * pack, the exit lanes and position, the markdown recommendation and refusals, the cycle status,
 * findings and trend, the impact strip, benefits, the five use-case tiles and the parameters
 * shortcut. Package C7 removed what was left over: the run line and the F16 notice in the body (to
 * the top bar), the "Data behind this page" chips (to the strip, and a feeds table in their place),
 * the per-row "Explain" buttons (the explain view is the queue page's), the empty-directory panel
 * (to the strip and the hero's sentence).
 *
 * ── THE PERIMETER IS RESOLVED, NAMED AND IN THE URL ──────────────────────────
 *
 * A landing screen carrying measures needs a perimeter, and G16 is explicit that a read whose
 * perimeter is not stated is a read over the whole estate. The perimeter is not invented: it is
 * the cycle run itself, resolved from the directory the platform publishes, named in the top bar
 * above every measure, and written into the URL so the view is a link rather than a moment.
 * `scope.ts` argues that in full (ADR 9003) and is the file to read before changing any of it.
 * Nothing else here defaults: no market, no period, no date (DEC-85).
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { listConsolidationRuns, inseasonListRuns } from '@/api/generated';
import { useApiQuery } from '@/api';
import { MOVE_RUN_ID_KEY } from '@/features/inseason/pending-links';
import {
  LayoutRow, NEWEST_RUN_OPTION, PAGE_CONTENT_BOX, PageStatusRegion, resolveHeadline, RunDefaultNote, useRunDefault,
} from '@/planning';

import { AppTiles, type AppTileId } from './AppTiles';
import { brokenDoorsHref, cockpitSynthesis, coverageTally, decisionRows, type D2Standing, type DecisionId } from './cockpit';
import { CockpitHero } from './CockpitHero';
import { CockpitKpis } from './CockpitKpis';
import { DataCoverage } from './DataCoverage';
import { decisionLink } from './decision-link';
import { NeedsDecision } from './NeedsDecision';
import { CockpitStatus, RunScopeInTopBar } from './panels';
import { useCockpitReads } from './useCockpitReads';
import {
  EXIT_LANES_RUN_ID_PARAM,
  EXIT_RUN_ID_PARAM,
  RUN_ID_PARAM,
  readOverviewScope,
  resolveD2Perimeter,
  resolveExitRun,
  priceRunIdOf,
} from './scope';

/**
 * How many runs "Change run" offers: one request, not a full cursor walk. A planner who needs an
 * older one reaches `/inseason/markdown`, whose picker walks the whole directory.
 */
const RUN_DIRECTORY_LIMIT = 10;

export interface OverviewScreenProps {
  /** The search string the shell already parsed. G2.2 — the URL is the state. */
  readonly params: Readonly<Record<string, string>>;
  /** Navigate, carrying or replacing the query string. */
  readonly onNavigate: (to: string, params?: Record<string, string>) => void;
  /** Replace the address bar without adding a history entry. Takes a patch, merged onto the live URL params, not a full replacement. */
  readonly onReplaceParams: (patch: Record<string, string>) => void;
}

/** The platform job whose runs write the consolidation position — bata-platform ADR 7019. */
const CONSOLIDATION_POSITION_JOB = 'exit.consolidation_position';

/** The platform job whose runs write the consolidation DECISIONS and lanes. */
const CONSOLIDATION_DECISION_JOB = 'exit.consolidation_run';

/** Data ▸ Feeds, and its market parameter — the page «Copertura dati» opens in full. */
const FEEDS_PATH = '/foundations/feeds';
const FEEDS_COUNTRY_PARAM = 'country';

/**
 * Where each app tile opens: the app's main page, on the run its figure is about — the price run's
 * calendar (its recommendation, which left the markdown Output in issue 226), allocation, the one
 * move run's report. The decision pages are "Needs a decision"'s.
 */
/** The memory scope of the Cockpit's cycle run (DEC-99): any in-season run, which names its market. */
const COCKPIT_RUN_SCOPE = { kind: 'inseason.any' } as const;

const APP_PATH: Readonly<Record<AppTileId, string>> = {
  pricing: '/inseason/markdown/calendar',
  replenishment: '/inseason/allocation',
  // The one run's report (owner ruling 2026-11-02): the transfer book is the retired rebalance runs' history.
  compacting: '/inseason/compacting-report',
};

export function OverviewScreen({
  params,
  onNavigate,
  onReplaceParams,
}: OverviewScreenProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const scope = readOverviewScope(params);

  // No `country_key` filter: `ScopedRows` already rewrites this read to the principal's
  // geography server-side (G3.1), so a filter here could only narrow what a planner is entitled
  // to see — and one that defaulted to a market would be this file deciding whose runs exist.
  const directory = useApiQuery(inseasonListRuns, { query: { limit: RUN_DIRECTORY_LIMIT } });
  const runs = directory.data?.items ?? [];
  /**
   * DEC-99 (`docs/adr/9039`): the URL's run, else the newest (`resolveRunId`'s rule; the URL is the
   * only memory, owner decision 2026-10-10) — written into the URL
   * with `replaceState` by the hook, once. Keyed as the in-season pages that read any run.
   */
  const offered = directory.isError ? [] : directory.data === undefined ? undefined : runs.map((row) => row.run_id);
  const memory = useRunDefault({
    scope: COCKPIT_RUN_SCOPE,
    urlRunId: scope.runId,
    offered,
    write: (chosen) => onReplaceParams({ [RUN_ID_PARAM]: chosen }),
  });
  const resolution = memory.resolution;
  const runId = resolution.source === 'url' || resolution.source === 'newest'
    ? resolution.runId
    : null;

  /** The exit side's position run (bata-platform ADR 7019): the newest recorded, one row. */
  const exitRuns = useApiQuery(listConsolidationRuns, {
    query: { job_name: CONSOLIDATION_POSITION_JOB, limit: 1 },
  });
  const exitRun = resolveExitRun(scope.exitRunId, exitRuns.data?.items[0]);

  /** The exit DECISION run — the run whose proposed lanes wait on a planner. One row, the newest. */
  const laneRuns = useApiQuery(listConsolidationRuns, {
    query: { job_name: CONSOLIDATION_DECISION_JOB, limit: 1 },
  });
  const laneRun = resolveExitRun(scope.exitLaneRunId, laneRuns.data?.items[0]);

  /**
   * Put each resolved run in the address bar, once, without a history entry: the planner
   * arrived, they did not navigate, and a resolved perimeter that is not in the URL cannot be
   * shared or re-read (G16).
   */
  useEffect(() => {
    if (scope.exitRunId !== null || exitRun === null) return;
    onReplaceParams({ [EXIT_RUN_ID_PARAM]: exitRun.runId });
  }, [scope.exitRunId, exitRun, onReplaceParams]);
  useEffect(() => {
    if (scope.exitLaneRunId !== null || laneRun === null) return;
    onReplaceParams({ [EXIT_LANES_RUN_ID_PARAM]: laneRun.runId });
  }, [scope.exitLaneRunId, laneRun, onReplaceParams]);

  const onscreenRun = runId === null ? undefined : runs.find((row) => row.run_id === runId);
  /** The market of the run on screen — the feeds' perimeter and the kicker's market. Never guessed. */
  const runCountry = onscreenRun?.country_key ?? null;
  /**
   * Which gate-D2 read the transfers row and the Compacting tile make: the dated read at the
   * URL's `?asOf=`, else each market's newest assessment — see `resolveD2Perimeter`.
   */
  const d2Perimeter = resolveD2Perimeter(scope.asOf);
  const reads = useCockpitReads({
    runId,
    runCountry,
    exitRunId: exitRun?.runId ?? null,
    directory,
    exitRuns,
    laneRuns,
    d2: d2Perimeter,
  });
  // Gate D2 in the run's own market, for the transfers row and the Compacting tile.
  const runMarket = reads.d2.state.kind === 'populated' && onscreenRun !== undefined
    ? reads.d2.figures.find((market) => market.country === onscreenRun.country_key)
    : undefined;
  const d2Standing: D2Standing = runMarket === undefined ? 'unknown' : runMarket.enabled ? 'enabled' : 'held';
  const decisions = decisionRows({
    candidates: reads.candidates.figures,
    prices: reads.prices.figures,
    decisions: reads.decisions.counts,
    priceDecisions: reads.priceDecisions,
    replenishment: reads.replenishment.figures,
    moves: reads.moves.figures,
    d2: d2Standing,
    openAlerts: reads.openAlerts,
    writeBack: reads.writeBack,
    ladderExpiry: reads.ladderExpiry,
    pricingCoverage: reads.pricingCoverage,
  }, i18n.language);
  const tally = coverageTally(reads.coverage);
  /** The run the markdown figures read — the cycle run when it has a price step, else (F16). */
  const priceRunId = priceRunIdOf(reads.priceRun);
  const settling = tally.loading > 0;
  /** The directory answered, and with no run: no cycle has recorded one. */
  const noRun = directory.data !== undefined && runId === null;
  const synthesis = resolveHeadline(cockpitSynthesis, { settling, runKnown: !noRun, decisions, tally }, i18n.language);

  /** The run, under the in-season chapter's own spelling. */
  const inseasonParams = (): Record<string, string> => (runId === null ? {} : { [RUN_ID_PARAM]: runId });
  /** The run the price figures were read from — the price run (F16) — else the cycle run. */
  const priceParams = (): Record<string, string> => (
    priceRunId === null ? inseasonParams() : { [RUN_ID_PARAM]: priceRunId }
  );
  /** Every row opens the page that lists exactly the rows it counted, ON THE RUN it counted (#148 step 1.5a). */
  const openDecision = (id: DecisionId): void => {
    const link = decisionLink(id, {
      runId, priceRunId, pricingCoverage: reads.pricingCoverage,
      transfersMoveRunId: reads.decisions.counts?.transfersMoveRunId ?? null,
    });
    onNavigate(link.path, link.params);
  };
  /** The one run's report on the move run the tile states (the summary's, else the market's newest), else its own newest. */
  const compactingParams = (): Record<string, string> => {
    const moveRunId = reads.moves.figures?.runId ?? reads.decisions.counts?.transfersMoveRunId ?? null;
    return moveRunId === null ? {} : { [MOVE_RUN_ID_KEY]: moveRunId };
  };
  const openApp = (app: AppTileId): void => {
    if (app === 'pricing') onNavigate(APP_PATH[app], priceParams());
    else if (app === 'compacting') onNavigate(APP_PATH[app], compactingParams());
    else onNavigate(APP_PATH[app], inseasonParams());
  };
  /** A run chosen in the top bar (the URL is its only memory); «Il più recente» follows the newest. */
  const chooseRun = (next: string): void => {
    if (next === NEWEST_RUN_OPTION) {
      memory.chooseNewest();
      const { [RUN_ID_PARAM]: _dropped, ...rest } = params;
      onNavigate('/', rest);
      return;
    }
    memory.choose(next);
    onNavigate('/', { ...params, [RUN_ID_PARAM]: next });
  };
  const priceRun = reads.priceRun;

  return (
    <>
      <CockpitHero country={runCountry} title={{ key: synthesis.key, values: synthesis.values }} />
      {/* The perimeter, in the top bar (designer item 2.7) — never in the body. */}
      {runId !== null && (
        <RunScopeInTopBar
          run={onscreenRun}
          runs={runs}
          current={runId}
          onChoose={chooseRun}
          priceRun={priceRun?.kind === 'moved'
            ? { runId: priceRun.runId, run: runs.find((row) => row.run_id === priceRun.runId) }
            : null}
        />
      )}
      <PageStatusRegion>
        {/* DEC-99: the newest is the Cockpit's subject and is named in the top bar already, so it
            carries no caption. */}
        <RunDefaultNote memoryKey={memory.key} runId={runId} showNewest={false} />
        <CockpitStatus coverage={reads.coverage} noRun={noRun} />
        {/* The page grid (brief part 3 §B5, ADR 9038): the hero on the full width above, then the
            KPIs on 12 columns, «Da decidere» on 8 beside «Copertura dati» on 4 at one height — the
            shorter table shows the rows it has and its frame runs down to the same base — and the
            apps side by side on equal columns. */}
        <div data-layout-content="" className={`${PAGE_CONTENT_BOX} flex flex-col gap-8 py-6`}>
          <LayoutRow id="cockpit-kpis" kind="full">
            <CockpitKpis
              exit={reads.exit}
              prices={reads.prices}
              candidates={reads.candidates}
              cycle={onscreenRun === undefined ? null : { country: onscreenRun.country_key, period: onscreenRun.period_key }}
              exitAsOf={exitRun?.asOf ?? null}
              brokenHref={brokenDoorsHref(exitRun?.runId ?? null, laneRuns.data?.items[0], scope.exitLaneRunId, reads.exit.figures?.brokenDoors ?? null)}
            />
          </LayoutRow>
          <LayoutRow id="cockpit-decide-coverage" kind="wide">
            <NeedsDecision rows={decisions} settling={settling} onOpen={openDecision} />
            <DataCoverage
              country={runCountry}
              state={runCountry === null ? null : reads.feeds.state}
              feeds={reads.feeds.rows}
              onOpenAll={() => onNavigate(FEEDS_PATH, runCountry === null ? {} : { [FEEDS_COUNTRY_PARAM]: runCountry })}
            />
          </LayoutRow>
          <AppTiles
            runKnown={runId !== null}
            prices={reads.prices}
            priceDecisions={reads.priceDecisions}
            replenishment={reads.replenishment}
            moves={reads.moves}
            decisions={reads.decisions.counts}
            d2={runMarket}
            onOpen={openApp}
          />
        </div>
      </PageStatusRegion>
    </>
  );
}

export default OverviewScreen;
